import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { environment } from "../../environments/environment";
import {
    ICountTotal,
    NameValueAmountDto
} from "../modules/shared/models/api-response.model";
import { AuthService } from "./auth.service";

const API_URL = environment.api;

@Injectable()
export class StatisticsService {
    constructor(
        public http: HttpClient,
        private _authService: AuthService
    ) {}

    private get _defaultOptions() {
        return { headers: this._headers };
    }
    private get _headers(): HttpHeaders {
        let h = new HttpHeaders();
        h = h.set("Authorization", `bearer ${this._authService.accessToken}`);
        h = h.set("Content-Type", "application/json");
        return h;
    }

    getOutstandingTotals(): Observable<ICountTotal> {
        return this.http
            .get(
                `${API_URL}/api/v1/stats/outstanding-totals`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getClaimTotals(): Observable<ICountTotal> {
        return this.http
            .get(`${API_URL}/api/v1/stats/claim-totals`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getForcingTotals(): Observable<ICountTotal> {
        return this.http
            .get(`${API_URL}/api/v1/stats/forcing-totals`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getExceptionTotals(): Observable<ICountTotal> {
        return this.http
            .get(
                `${API_URL}/api/v1/stats/exception-totals`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getExposureTotals(): Observable<ICountTotal> {
        return this.http
            .get(
                `${API_URL}/api/v1/stats/exposure-totals`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getInsufficientTotals(): Observable<ICountTotal> {
        return this.http
            .get(
                `${API_URL}/api/v1/stats/insufficient-totals`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getExposure(
        filter: string | null = null,
        accounts: string[] | null = null,
        departments: string[] | null = null,
        coverages: string[] | null = null,
        insuranceStatuses: string[] | null = null,
        reasons: string[] | null = null,
        customTypes: string[] | null = null,
        loanConditions: string[] | null = null
    ): Observable<ICountTotal[]> {
        let p = {};

        if (filter) {
            p["filter"] = filter;
        }

        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }

        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }

        if (coverages && coverages.length > 0) {
            p["coverages"] = coverages.join(",");
        }

        if (insuranceStatuses && insuranceStatuses.length > 0) {
            p["insurance-statuses"] = insuranceStatuses.join(",");
        }

        if (reasons && reasons.length > 0) {
            p["reasons"] = reasons.join(",");
        }

        if (customTypes && customTypes.length > 0) {
            let c = customTypes
                .filter((c) => c !== "ICL")
                .filter((c) => c != null);
            p["custom-types"] = c.join(",");
        }

        if (loanConditions && loanConditions.length > 0) {
            p["loan-conditions"] = loanConditions.join(",");
        }

        let options = { headers: this._headers, params: p };
        return this.http
            .get(`${API_URL}/api/v1/stats/exposure`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getExposureByCoverageTypeTotals(
        filter: string | null = null,
        accounts: string[] | null = null,
        departments: string[] | null = null,
        coverages: string[] | null = null,
        insuranceStatuses: string[] | null = null,
        reasons: string[] | null = null,
        customTypes: string[] | null = null,
        loanConditions: string[] | null = null
    ) {
        const p = {};

        if (filter) {
            p["filter"] = filter;
        }

        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }

        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }

        if (coverages && coverages.length > 0) {
            p["coverages"] = coverages.join(",");
        }

        if (insuranceStatuses && insuranceStatuses.length > 0) {
            p["insurance-statuses"] = insuranceStatuses.join(",");
        }

        if (reasons && reasons.length > 0) {
            p["reasons"] = reasons.join(",");
        }

        if (customTypes && customTypes.length > 0) {
            const c = customTypes
                .filter((c) => c !== "ICL")
                .filter((c) => c != null);
            p["custom-types"] = c.join(",");
        }

        if (loanConditions && loanConditions.length > 0) {
            p["loan-conditions"] = loanConditions.join(",");
        }

        const options = { headers: this._headers, params: p };

        return this.http.get<NameValueAmountDto[]>(
            `${API_URL}/api/v1/stats/exposure-coverage-type`,
            options
        );
    }

    getException(
        filter: string | null = null,
        accounts: string[] | null = null,
        departments: string[] | null = null,
        coverages: string[] | null = null,
        deficiencies: string[] | null = null,
        customTypes: string[] | null = null,
        loanConditions: string[] | null = null
    ): Observable<ICountTotal[]> {
        let p = {};

        if (filter) {
            p["filter"] = filter;
        }

        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }

        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }

        if (coverages && coverages.length > 0) {
            p["coverages"] = coverages.join(",");
        }

        if (customTypes && customTypes.length > 0) {
            let c = customTypes
                .filter((c) => c !== "ICL")
                .filter((c) => c != null);
            p["custom-types"] = c.join(",");
        }

        if (loanConditions && loanConditions.length > 0) {
            p["loan-conditions"] = loanConditions.join(",");
        }

        if (deficiencies && deficiencies.length > 0) {
            p["deficiencies"] = deficiencies.join(",");
        }

        let options = { headers: this._headers, params: p };

        return this.http
            .get(`${API_URL}/api/v1/stats/exception`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getForcing(
        filter: string | null = null,
        accounts: string[] | null = null,
        departments: string[] | null = null,
        coverages: string[] | null = null,
        insuranceStatuses: string[] | null = null,
        startAt: Date | null = null,
        endAt: Date | null = null,
        customTypes: string[] | null = null,
        loanConditions: string[] | null = null
    ): Observable<ICountTotal[]> {
        let p = {};

        if (filter) {
            p["filter"] = filter;
        }

        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }

        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }

        if (coverages && coverages.length > 0) {
            p["coverages"] = coverages.join(",");
        }

        if (startAt) {
            p["start-at"] = startAt.toISOString().split("T")[0];
        }

        if (endAt) {
            p["end-at"] = endAt.toISOString().split("T")[0];
        }

        if (customTypes && customTypes.length > 0) {
            let c = customTypes
                .filter((c) => c !== "ICL")
                .filter((c) => c != null);
            p["custom-types"] = c.join(",");
        }

        if (loanConditions && loanConditions.length > 0) {
            p["loan-conditions"] = loanConditions.join(",");
        }

        if (insuranceStatuses) {
            p["insurance-statuses"] = insuranceStatuses.join(",");
        }

        let options = { headers: this._headers, params: p };
        return this.http
            .get(`${API_URL}/api/v1/stats/forcing`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }
}
